import React from 'react';
import { useStaticQuery, graphql, Link, withPrefix } from 'gatsby';
import styled, { x, useTheme, th } from '@xstyled/styled-components';
import { useMenuState, Menu, MenuItem, MenuButton } from 'reakit/Menu';
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';
import {
  useDisclosureState,
  Disclosure,
  DisclosureContent
} from 'reakit/Disclosure';
import { useSidenavDialog } from '../context/SidenavDialogContext';

const SideNavQuery1 = graphql`
  query SideNavQuery1 {
    allMdx(filter: { fields: { pageType: { eq: "doc" }, title: { ne: "" } } }) {
      edges {
        node {
          id
          fields {
            title
            pageType
            section
            order
            slug
          }
        }
      }
    }

    site {
      siteMetadata {
        sections
      }
    }
  }
`;

const createOrFindGroup = (name, groups) => {
  const existingGroup = groups.find(group => group.name === name);
  if (existingGroup) return existingGroup;

  const group = { name, nodes: [] };
  groups.push(group);
  return group;
};

const DEFAULT_ORDER_VALUE = -9999;

const sortNodes = (a, b) => {
  const diff =
    a.fields.order !== DEFAULT_ORDER_VALUE &&
    b.fields.order !== DEFAULT_ORDER_VALUE
      ? a.fields.order - b.fields.order
      : a.fields.order !== DEFAULT_ORDER_VALUE
      ? -1
      : b.fields.order !== DEFAULT_ORDER_VALUE
      ? 1
      : a.fields.title.localeCompare(b.fields.title);
  return diff === 0 ? 0 : diff > 0 ? 1 : -1;
};

const groupNodes = nodes =>
  nodes.reduce((groups, node) => {
    const group = createOrFindGroup(node.fields.section || 'Docs', groups);
    group.nodes.push(node);
    group.nodes.sort(sortNodes);
    return groups;
  }, []);

const Nav = styled.nav`
  font-family: 'cabin';
  padding: 4 4;
`;

const NavGroupTitle = styled.span`
  font-size: sm;
  font-weight: bold;
  line-height: 4;
  color: on-background-light;
  text-transform: uppercase;
  padding: 2 0;
  display: flex;
  align-items: center;

  svg {
    margin-left: 4;
  }

  a {
    color: on-background-light;
    transition: fast;
    transition-property: color, border-color;
    text-decoration: none;

    &[aria-current='page'] {
      color: primary;
    }
  }
`;

const NavGroupMenu = styled.ul`
  margin: 0;
  padding: 0;
  border-left: 1;
  border-left-color: #7b99a1;
`;

const NavGroupMenuItem = styled.li`
  list-style-type: none;
  margin: 2 0 0 -2px;
  padding: 0;
  font-size: sm;
  font-weight: 500;

  a {
    color: on-background-light;
    display: block;
    transition: fast;
    transition-property: color, border-color;
    padding: 1 0 1 2;
    border-left: 2;
    border-color: transparent;
    text-decoration: none;

    &:hover {
      color: on-background;
    }

    &[aria-current='page'] {
      font-weight: 700;
      border-color: primary;
      color: primary;
    }
  }
`;

const NavGroup = ({ group }) => {
  const disclosure = useDisclosureState();
  const dialog = useSidenavDialog();

  return (
    <x.div mb="2">
      <Disclosure
        {...disclosure}
        css={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          padding: 0,
          '&:focus': {
            outline: 'none'
          }
        }}
      >
        <NavGroupTitle fontSize="sm">
          {group.name}
          {disclosure.visible ? (
            <svg
              width="12"
              height="7"
              viewBox="0 0 12 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6 0L0 5.67568L1.4 7L6 2.64865L10.6 7L12 5.67568L6 0Z"
                fill="#0FA789"
              />
            </svg>
          ) : (
            <svg
              width="12"
              height="7"
              viewBox="0 0 12 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6 7L0 1.32432L1.4 0L6 4.35135L10.6 0L12 1.32432L6 7Z"
                fill="#0FA789"
              />
            </svg>
          )}
        </NavGroupTitle>
      </Disclosure>
      <DisclosureContent {...disclosure}>
        <NavGroupMenu>
          {group.nodes.map(page => (
            <NavGroupMenuItem key={page.id}>
              <Link to={page.fields.slug} onClick={dialog.hide}>
                {page.fields.title}
              </Link>
            </NavGroupMenuItem>
          ))}
        </NavGroupMenu>
      </DisclosureContent>
    </x.div>
  );
};

const sortGroupsWithConfig = section => (a, b) => {
  const indexA = section.indexOf(a.name);
  const indexB = section.indexOf(b.name);
  const diff = indexA - indexB;
  return diff === 0 ? 0 : diff < 0 ? -1 : 1;
};

export function useSideNavState(
  versions,
  currentVersion,
  latestVersionSiteUrl
) {
  const data = useStaticQuery(SideNavQuery1);
  const sections = data.site.siteMetadata.sections;
  const edges = data.allMdx.edges;

  return React.useMemo(() => {
    const navGroups = groupNodes(edges.map(edge => edge.node));
    navGroups.sort(sortGroupsWithConfig(sections));
    return { navGroups, versions, currentVersion, latestVersionSiteUrl };
  }, [edges, sections, versions, currentVersion, latestVersionSiteUrl]);
}

export function useSideNavPrevNext({ navGroups }) {
  const { pathname } = useLocation();
  const nodes = navGroups.flatMap(group => group.nodes);
  const nodeIndex = nodes.findIndex(
    node => withPrefix(node.fields.slug) === pathname
  );
  return {
    prev: nodeIndex > -1 ? nodes[nodeIndex - 1] : null,
    next: nodeIndex > -1 ? nodes[nodeIndex + 1] : null
  };
}

export function SideNav({
  navGroups,
  versions,
  currentVersion,
  latestVersionSiteUrl
}) {
  const menu = useMenuState();
  const theme = useTheme();

  return (
    <Nav>
      {versions && versions.length > 1 && (
        <x.div mb="8">
          <x.div fontSize="sm" lineHeight="1.5" mb="1">
            Version
          </x.div>
          <MenuButton
            {...menu}
            css={{
              width: '10rem',
              display: 'flex',
              alignItems: 'center',
              border: `1px solid ${theme.colors['primary']}`,
              borderRadius: '6px',
              lineHeight: 1,
              padding: '.5rem 1rem',
              '&[aria-expanded="true"]': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              },
              '&:focus': {
                outline: 'none'
              }
            }}
          >
            <x.span flexGrow="1" textAlign="left">
              {currentVersion}
            </x.span>
            <svg
              width="12"
              height="7"
              viewBox="0 0 12 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6 7L0 1.32432L1.4 0L6 4.35135L10.6 0L12 1.32432L6 7Z"
                fill="#0FA789"
              />
            </svg>
          </MenuButton>
          <Menu
            {...menu}
            tabIndex={0}
            aria-label="Versions"
            css={{
              width: '10rem',
              borderRight: `1px solid ${theme.colors['primary']}`,
              borderBottom: `1px solid ${theme.colors['primary']}`,
              borderLeft: `1px solid ${theme.colors['primary']}`,
              borderBottomLeftRadius: '6px',
              borderBottomRightRadius: '6px'
            }}
          >
            {versions.map((version, index, arr) => (
              <MenuItem
                {...menu}
                key={version}
                href={`${latestVersionSiteUrl}/${
                  index === 0 ? '' : `_v${version}/`
                }`}
                as="a"
                css={{
                  display: 'block',
                  textDecoration: 'none',
                  lineHeight: 1,
                  padding: '.5rem 1rem',
                  background: 'white',
                  color: th('colors.on-background-light')({ theme }),
                  '&:hover': {
                    color: th('colors.on-background')({ theme })
                  },
                  // if last item
                  ...(arr.length - 1 === index
                    ? {
                        borderBottomLeftRadius: '6px',
                        borderBottomRightRadius: '6px'
                      }
                    : {})
                }}
              >
                {version}
              </MenuItem>
            ))}
          </Menu>
        </x.div>
      )}
      <x.div mb="2">
        <NavGroupTitle>
          <Link to="/">Overview</Link>
        </NavGroupTitle>
      </x.div>
      {navGroups.map((g, index) => (
        <NavGroup key={index} group={g} />
      ))}
    </Nav>
  );
}
